import api from "../api";
import { notify } from "react-notify-toast";
import { t } from "../i18n";

class ScheduleCopyHelper {
   constructor() {
      this.selectedTasks = {};
      this.selectedUsers = {};
      this.isRequesting = false;
      this.warningVisible = false;
      this.warningConflicts = null;
      this.verificationData = null;
      this.currentUsers = null;
   }

   preparePayload(selectedProductKey, leadingDay, users) {
      const payload = {
         kodProduktu: selectedProductKey,
         dataDniaWiodacego: leadingDay || null,
         uzytkownicy: []
      };

      users.forEach((user) => {
         const userId = user.id;

         if (
            !this.selectedUsers[userId] &&
            !(
               user.zadania &&
               user.zadania.some((task) => {
                  const selectionKey = `${userId}_${task.id}`;
                  return this.selectedTasks[selectionKey];
               })
            )
         ) {
            return;
         }

         const userTasks = [];

         if (user.zadania && user.zadania.length > 0) {
            user.zadania.forEach((task) => {
               const selectionKey = `${userId}_${task.id}`;
               if (this.selectedTasks[selectionKey]) {
                  userTasks.push(task.id);
               }
            });
         }

         if (this.selectedUsers[userId] || userTasks.length > 0) {
            payload.uzytkownicy.push({
               uzytkownikId: userId,
               zadaniaIds: userTasks
            });
         }
      });

      return payload;
   }

   verifyScheduleCopy(payload, users, onComplete) {
      this.isRequesting = true;

      api.copyScheduleVerification(payload)
         .then((response) => {
            const { data } = response;
            if (data && Object.keys(data).length > 0) {
               this.warningVisible = true;
               // Hand back the RAW conflict map, not a formatted string: the caller renders it
               // through formatConflictMessage on every render so it follows the language switch.
               this.warningConflicts = { conflicts: data, users };
               this.verificationData = payload;
               onComplete(true, this.warningConflicts);
               return;
            }
            this.processCopySchedule(payload, onComplete);
         })
         .catch((error) => {
            console.error("Error verifying copy:", error);
            notify.show(t("toasts.copyVerificationError"), "error", 3000);
            onComplete(false);
         })
         .finally(() => {
            this.isRequesting = false;
         });
   }

   processCopySchedule(payload, onComplete) {
      this.isRequesting = true;

      api.copySchedule(payload)
         .then(() => {
            notify.show(t("toasts.scheduleCopied"), "success", 3000);
            this.reset();
            onComplete(true);
         })
         .catch((error) => {
            console.error("Error copying scheduler:", error);
            notify.show(t("toasts.copyError"), "error", 3000);
            onComplete(false);
         })
         .finally(() => {
            this.isRequesting = false;
         });
   }

   reset() {
      this.selectedTasks = {};
      this.selectedUsers = {};
      this.isRequesting = false;
      this.warningVisible = false;
      this.warningConflicts = null;
      this.verificationData = null;
      this.currentUsers = null;
   }

   toggleTaskSelection(taskId, userId) {
      const selectionKey = `${userId}_${taskId}`;
      this.selectedTasks[selectionKey] = !this.selectedTasks[selectionKey];
      
      if (this.currentUsers) {
         const user = this.currentUsers.find(u => u.id.toString() === userId.toString());
         if (user && user.zadania && user.zadania.length > 0) {
            const allUserTasksSelected = user.zadania.every(task => {
               const key = `${userId}_${task.id}`;
               return !!this.selectedTasks[key];
            });
            
            this.selectedUsers[userId] = allUserTasksSelected;
         }
      }
   }

   toggleUserSelection(userId) {
      this.selectedUsers[userId] = !this.selectedUsers[userId];
   }

   toggleUserTasksSelection(userId, user) {
      if (!user) return;
      
      if (!this.currentUsers) {
         this.currentUsers = [];
      }
      if (!this.currentUsers.find(u => u.id === user.id)) {
         this.currentUsers.push(user);
      }

      if (user.zadania && user.zadania.length > 0) {
         const allSelected = user.zadania.every((task) => {
            const selectionKey = `${userId}_${task.id}`;
            return this.selectedTasks[selectionKey];
         });

         user.zadania.forEach((task) => {
            const selectionKey = `${userId}_${task.id}`;
            this.selectedTasks[selectionKey] = !allSelected;
         });

         this.selectedUsers[userId] = !allSelected;
         return;
      }
      this.toggleUserSelection(userId);
   }

   toggleSelectAll(users, week) {
      if (!this.currentUsers) {
         this.currentUsers = [];
      }
      users.forEach(user => {
         if (!this.currentUsers.find(u => u.id === user.id)) {
            this.currentUsers.push(user);
         }
      });
      
      const userIds = [];
      const weekTasksWithUsers = [];

      users.forEach((user) => {
         const userId = user.id.toString();
         userIds.push(userId);

         if (user.zadania && user.zadania.length > 0) {
            user.zadania.forEach((task) => {
               if (week.includes(task.data)) {
                  weekTasksWithUsers.push({
                     taskId: task.id,
                     userId: userId
                  });
               }
            });
         }
      });

      const allSelected =
         userIds.length > 0 &&
         userIds.every((id) => this.selectedUsers[id]) &&
         (weekTasksWithUsers.length === 0 ||
            weekTasksWithUsers.every((task) => {
               const selectionKey = `${task.userId}_${task.taskId}`;
               return this.selectedTasks[selectionKey];
            }));

      weekTasksWithUsers.forEach((task) => {
         const selectionKey = `${task.userId}_${task.taskId}`;
         this.selectedTasks[selectionKey] = !allSelected;
      });

      userIds.forEach((id) => {
         this.selectedUsers[id] = !allSelected;
      });
   }

   toggleDayTasksSelection(day, users) {
      if (!this.currentUsers) {
         this.currentUsers = [];
      }
      users.forEach(user => {
         if (!this.currentUsers.find(u => u.id === user.id)) {
            this.currentUsers.push(user);
         }
      });
      
      const dayTasksWithUsers = [];
      users.forEach((user) => {
         const userId = user.id.toString();
         (user.zadania || []).forEach((task) => {
            if (task.data === day) {
               dayTasksWithUsers.push({
                  taskId: task.id,
                  userId: userId
               });
            }
         });
      });

      if (dayTasksWithUsers.length === 0) return;

      const allDayTasksSelected = dayTasksWithUsers.every((task) => {
         const selectionKey = `${task.userId}_${task.taskId}`;
         return this.selectedTasks[selectionKey];
      });

      dayTasksWithUsers.forEach((task) => {
         const selectionKey = `${task.userId}_${task.taskId}`;
         this.selectedTasks[selectionKey] = !allDayTasksSelected;
         this.selectedUsers[task.userId] = !allDayTasksSelected;
      });
   }

   areDayTasksSelected(day, users) {
      const dayTasksWithUsers = [];
      users.forEach((user) => {
         const userId = user.id.toString();
         (user.zadania || []).forEach((task) => {
            if (task.data === day) {
               dayTasksWithUsers.push({
                  taskId: task.id,
                  userId: userId
               });
            }
         });
      });

      if (dayTasksWithUsers.length === 0) return false;

      return dayTasksWithUsers.every((task) => {
         const selectionKey = `${task.userId}_${task.taskId}`;
         return this.selectedTasks[selectionKey];
      });
   }

   areAllWeekTasksSelected(users, week) {
      const weekTasksWithUsers = [];
      const userIds = [];

      users.forEach((user) => {
         const userId = user.id.toString();
         userIds.push(userId);

         if (user.zadania && user.zadania.length > 0) {
            user.zadania.forEach((task) => {
               if (week.includes(task.data)) {
                  weekTasksWithUsers.push({
                     taskId: task.id,
                     userId: userId
                  });
               }
            });
         }
      });

      if (userIds.length === 0) return false;

      return (
         userIds.every((id) => this.selectedUsers[id]) &&
         (weekTasksWithUsers.length === 0 ||
            weekTasksWithUsers.every((task) => {
               const selectionKey = `${task.userId}_${task.taskId}`;
               return this.selectedTasks[selectionKey];
            }))
      );
   }

   hasAnySelection() {
      const hasSelectedTasks = Object.values(this.selectedTasks).some((selected) => selected);
      const hasSelectedUsers = Object.values(this.selectedUsers).some((selected) => selected);

      return hasSelectedTasks || hasSelectedUsers;
   }
}

export default ScheduleCopyHelper;
